import { useState, useEffect } from 'react';
import { BACKEND_URL } from '@/lib/env';
import { usePermissions } from '@/contexts/PermissionsContext';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import { coerceSettingsTab, defaultSettingsTab, SETTINGS_TABS, canViewSettingsTab } from '@/lib/settingsAccess';
import SettingsUserManagement from '@/components/SettingsUserManagement';
import SettingsGroupManagement from '@/components/SettingsGroupManagement';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Settings as SettingsIcon, Database, Zap, Bell, Shield, Save, Server, Plus, Trash2, Edit, CheckCircle, XCircle, AlertCircle, Users, Plug } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { EnhancedSAPSystemConfig } from '@/components/EnhancedSAPSystemConfig';
import SettingsMcpServers from '@/components/SettingsMcpServers';
import { fetchGeneralSettings, isChatConnected, isTicketConnected, updateGeneralSettings } from '@/lib/generalSettingsApi';

const API = `${BACKEND_URL}/api`;

export default function Settings() {
  const { hasPermission, hasPermissionKey } = usePermissions();
  const tabAccess = { hasPermissionKey };
  const isPlatformAdmin = hasPermissionKey("settings.user-management:read");
  const canWriteGeneral = hasPermission('settings.general', 'update')
    || hasPermissionKey('settings.general:write');
  const canWriteSapLandscape = hasPermission('settings.sap-landscape', 'create')
    || hasPermission('settings.sap-landscape', 'update')
    || hasPermission('settings.sap-landscape', 'delete')
    || hasPermissionKey('settings.sap-landscape:write');
  const [activeTab, setActiveTab] = useState(() => defaultSettingsTab(tabAccess));
  const [saved, setSaved] = useState(false);
  const [sapSystems, setSapSystems] = useState([]);
  const [addSystemDialog, setAddSystemDialog] = useState(false);
  const [editingSystem, setEditingSystem] = useState(null);
  const [newSystem, setNewSystem] = useState({
    name: '',
    type: 'S/4HANA Cloud',
    version: '',
    environment: 'Production',
    url: '',
    client: '100',
    username: '',
    password: '',
    location: 'US-East',
    description: '',
    
    // Environment Architecture
    architecture_type: 'single_system_multi_client', // or 'separate_systems' or 'btp_spaces'
    dev_client: '100',
    qa_client: '200',
    prod_client: '300',
    dev_url: '',
    qa_url: '',
    prod_url: '',
    
    // Access Configuration
    access_types: {
      http_rest: true,
      odata_api: false,
      cloud_foundry_api: false,
      adt_api: false,
      rfc_access: false,
      transport_api: false
    },
    
    // Cloud Foundry Configuration (if BTP)
    cf_api_endpoint: '',
    cf_org: '',
    cf_space_dev: '',
    cf_space_qa: '',
    cf_space_prod: '',
    cf_username: '',
    cf_password: '',
    
    // OData/REST API Configuration
    odata_base_url: '',
    api_username: '',
    api_password: '',
    
    // Transport System Configuration
    transport_domain: '',
    transport_group: ''
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [settings, setSettings] = useState({
    // General Settings
    platformName: 'SAP AI Customization Engine',
    environment: 'production',
    timezone: 'UTC',
    language: 'en',
    
    // Agent Settings
    defaultLLM: 'gpt-4o-mini',
    agentTimeout: 300,
    maxRetries: 3,
    parallelAgents: false,
    
    // Notification Settings
    emailNotifications: true,
    slackNotifications: false,
    webhookUrl: '',
    notifyOnComplete: true,
    notifyOnFailure: true,
    
    // Integration Settings
    jiraUrl: 'https://your-domain.atlassian.net',
    sapBtpUrl: 'https://btp.example.com',
    
    // Security Settings
    requireApproval: true,
    twoFactorAuth: false,
    sessionTimeout: 3600,
    auditLogging: true,
    
    // Performance Settings
    cacheEnabled: true,
    cacheDuration: 3600,
    maxConcurrentTasks: 10,
    autoRefresh: true,
    refreshInterval: 5000,

    // Platform connected mode
    chatConnectedMode: true,
    ticketConnectedMode: true,
  });

  useEffect(() => {
    setActiveTab((prev) => coerceSettingsTab(prev, tabAccess));
  }, [hasPermissionKey]);

  useEffect(() => {
    if (canViewSettingsTab(SETTINGS_TABS.SAP_LANDSCAPE, tabAccess)) {
      loadSapSystems();
    }
    if (canViewSettingsTab(SETTINGS_TABS.GENERAL, tabAccess)) {
      loadGeneralSettings();
    }
  }, [hasPermissionKey]);

  const loadGeneralSettings = async () => {
    try {
      const data = await fetchGeneralSettings();
      setSettings((prev) => ({
        ...prev,
        chatConnectedMode: isChatConnected(data),
        ticketConnectedMode: isTicketConnected(data),
      }));
    } catch (error) {
      console.error('Error loading general settings:', error);
    }
  };

  const handleSettingsTabChange = (value) => {
    setActiveTab(coerceSettingsTab(value, tabAccess));
  };

  const loadSapSystems = async () => {
    try {
      const response = await axios.get(`${API}/settings/sap-landscape/systems`);
      setSapSystems(response.data);
    } catch (error) {
      console.error('Error loading SAP systems:', error);
      // Load mock data if backend not available
      setSapSystems([
        {
          id: '1',
          name: 'S/4HANA Production',
          type: 'S/4HANA Cloud',
          version: '2023',
          environment: 'Production',
          url: 'https://s4hana-prod.company.com',
          client: '100',
          location: 'US-East',
          status: 'connected',
          lastConnection: new Date().toISOString()
        },
        {
          id: '2',
          name: 'SAP BTP Production',
          type: 'SAP BTP',
          version: 'Cloud',
          environment: 'Production',
          url: 'https://btp-prod.cfapps.us10.hana.ondemand.com',
          location: 'US-East',
          status: 'connected',
          lastConnection: new Date().toISOString()
        }
      ]);
    }
  };

  const handleAddSystem = async (systemData) => {
    try {
      const systemWithId = { ...systemData, id: Date.now().toString(), status: 'configured' };
      
      // Save to backend
      await axios.post(`${API}/settings/sap-landscape/systems`, systemData);
      
      setSapSystems([...sapSystems, systemWithId]);
      setAddSystemDialog(false);
      resetNewSystem();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      // If backend not available, just add locally
      const systemWithId = { ...systemData, id: Date.now().toString(), status: 'configured' };
      setSapSystems([...sapSystems, systemWithId]);
      setAddSystemDialog(false);
      resetNewSystem();
    }
  };

  const handleDeleteSystem = async (id) => {
    if (!canWriteSapLandscape) return;
    try {
      await axios.delete(`${API}/settings/sap-landscape/systems/${id}`);
      setSapSystems(sapSystems.filter(s => s.id !== id));
    } catch (error) {
      // If backend not available, just delete locally
      setSapSystems(sapSystems.filter(s => s.id !== id));
    }
  };

  const handleTestConnection = async (system) => {
    setTestingConnection(true);
    setConnectionStatus(null);
    
    try {
      // In production, this would actually test the connection
      await new Promise(resolve => setTimeout(resolve, 2000));
      const success = Math.random() > 0.3; // Mock success/failure
      
      setConnectionStatus({ 
        success, 
        message: success ? 'Connection successful!' : 'Connection failed. Check credentials.' 
      });
      
      if (success) {
        setSapSystems(sapSystems.map(s => 
          s.id === system.id ? { ...s, status: 'connected', lastConnection: new Date().toISOString() } : s
        ));
      }
    } catch (error) {
      setConnectionStatus({ success: false, message: 'Connection test failed' });
    } finally {
      setTestingConnection(false);
      setTimeout(() => setConnectionStatus(null), 5000);
    }
  };

  const resetNewSystem = () => {
    setNewSystem({
      name: '',
      type: 'S/4HANA Cloud',
      version: '',
      environment: 'Production',
      url: '',
      client: '100',
      username: '',
      password: '',
      location: 'US-East',
      description: ''
    });
  };

  const handleSave = async () => {
    if (!canWriteGeneral) return;
    try {
      await updateGeneralSettings({
        chat_connected_mode: settings.chatConnectedMode ? 'connected' : 'disconnected',
        ticket_connected_mode: settings.ticketConnectedMode ? 'connected' : 'disconnected',
      });
      window.dispatchEvent(new Event('general-settings-updated'));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  const updateSetting = (key, value) => {
    if (!canWriteGeneral) return;
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground mt-2">
            {isPlatformAdmin
              ? 'Configure platform and integration settings'
              : 'Configure your notification and general preferences'}
          </p>
        </div>
        <span title={permissionDeniedTitle(canWriteGeneral, 'save general settings')}>
          <Button onClick={handleSave} disabled={!canWriteGeneral} data-testid="save-settings-btn">
            <Save className="mr-2 h-4 w-4" />
            Save Changes
          </Button>
        </span>
      </div>

      {saved && (
        <Alert className="bg-cp-inset border-cp-line">
          <AlertDescription className="text-cp-ok">Settings saved successfully!</AlertDescription>
        </Alert>
      )}

      <Tabs
        key={isPlatformAdmin ? 'settings-admin' : 'settings-user'}
        value={activeTab}
        onValueChange={handleSettingsTabChange}
        className="w-full"
      >
        <TabsList>
          {canViewSettingsTab(SETTINGS_TABS.SAP_LANDSCAPE, tabAccess) && (
            <TabsTrigger value={SETTINGS_TABS.SAP_LANDSCAPE}>
              <Server className="mr-2 h-4 w-4" />
              SAP Landscape
            </TabsTrigger>
          )}
          {canViewSettingsTab(SETTINGS_TABS.MCP_SERVERS, tabAccess) && (
            <TabsTrigger value={SETTINGS_TABS.MCP_SERVERS}>
              <Plug className="mr-2 h-4 w-4" />
              MCP Servers
            </TabsTrigger>
          )}
          {canViewSettingsTab(SETTINGS_TABS.GENERAL, tabAccess) && (
            <TabsTrigger value={SETTINGS_TABS.GENERAL}>General</TabsTrigger>
          )}
          {canViewSettingsTab(SETTINGS_TABS.AGENTS, tabAccess) && (
            <TabsTrigger value={SETTINGS_TABS.AGENTS}>Agents</TabsTrigger>
          )}
          {canViewSettingsTab(SETTINGS_TABS.INTEGRATIONS, tabAccess) && (
            <TabsTrigger value={SETTINGS_TABS.INTEGRATIONS}>Integrations</TabsTrigger>
          )}
          {canViewSettingsTab(SETTINGS_TABS.NOTIFICATIONS, tabAccess) && (
            <TabsTrigger value={SETTINGS_TABS.NOTIFICATIONS}>Notifications</TabsTrigger>
          )}
          {canViewSettingsTab(SETTINGS_TABS.SECURITY, tabAccess) && (
            <TabsTrigger value={SETTINGS_TABS.SECURITY}>Security</TabsTrigger>
          )}
          {canViewSettingsTab(SETTINGS_TABS.PERFORMANCE, tabAccess) && (
            <TabsTrigger value={SETTINGS_TABS.PERFORMANCE}>Performance</TabsTrigger>
          )}
          {canViewSettingsTab(SETTINGS_TABS.USER_MANAGEMENT, tabAccess) && (
            <TabsTrigger value={SETTINGS_TABS.USER_MANAGEMENT}>
              <Users className="mr-2 h-4 w-4" />
              User management
            </TabsTrigger>
          )}
          {canViewSettingsTab(SETTINGS_TABS.GROUP_MANAGEMENT, tabAccess) && (
            <TabsTrigger value={SETTINGS_TABS.GROUP_MANAGEMENT}>
              <Shield className="mr-2 h-4 w-4" />
              Group management
            </TabsTrigger>
          )}
        </TabsList>

        {/* SAP Landscape Configuration Tab */}
        {canViewSettingsTab(SETTINGS_TABS.SAP_LANDSCAPE, tabAccess) && (
        <TabsContent value={SETTINGS_TABS.SAP_LANDSCAPE} className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="h-5 w-5 text-cp-indigo" />
                    SAP Landscape Configuration
                  </CardTitle>
                  <CardDescription>Configure SAP systems for discovery and integration</CardDescription>
                </div>
                <Dialog open={addSystemDialog} onOpenChange={setAddSystemDialog}>
                  <span title={permissionDeniedTitle(canWriteSapLandscape, 'add SAP systems')}>
                    <DialogTrigger asChild>
                      <Button disabled={!canWriteSapLandscape}>
                        <Plus className="mr-2 h-4 w-4" />
                        Add SAP System
                      </Button>
                    </DialogTrigger>
                  </span>
                </Dialog>
                
                {/* Use Enhanced Config Dialog */}
                <EnhancedSAPSystemConfig
                  open={addSystemDialog}
                  onOpenChange={setAddSystemDialog}
                  system={newSystem}
                  onSave={handleAddSystem}
                />
              </div>
            </CardHeader>
            <CardContent>
              {sapSystems.length === 0 ? (
                <div className="text-center py-12">
                  <Server className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-semibold mb-2">No SAP Systems Configured</h3>
                  <p className="text-muted-foreground mb-4">
                    Add your SAP systems to enable discovery and integration
                  </p>
                  <span title={permissionDeniedTitle(canWriteSapLandscape, 'add SAP systems')}>
                    <Button onClick={() => setAddSystemDialog(true)} disabled={!canWriteSapLandscape}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add First System
                    </Button>
                  </span>
                </div>
              ) : (
                <div className="space-y-4">
                  {sapSystems.map((system) => (
                    <Card key={system.id} className="border-2">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{system.name}</h3>
                              <Badge variant={
                                system.environment === 'Production' ? 'default' :
                                system.environment === 'Development' ? 'secondary' : 'outline'
                              }>
                                {system.environment}
                              </Badge>
                              {system.status === 'connected' ? (
                                <Badge className="bg-cp-ok">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Connected
                                </Badge>
                              ) : (
                                <Badge variant="outline">
                                  Configured
                                </Badge>
                              )}
                              {/* Deployment Readiness Badge */}
                              {system.architecture_type && system.access_types && (
                                <Badge className="bg-cp-indigo">
                                  <Server className="h-3 w-3 mr-1" />
                                  Deploy Ready
                                </Badge>
                              )}
                              {!system.architecture_type && (
                                <Badge variant="destructive">
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  Config Incomplete
                                </Badge>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                              <div>
                                <div className="text-muted-foreground text-xs">Type</div>
                                <div className="font-medium">{system.type}</div>
                              </div>
                              {system.version && (
                                <div>
                                  <div className="text-muted-foreground text-xs">Version</div>
                                  <div className="font-medium">{system.version}</div>
                                </div>
                              )}
                              <div>
                                <div className="text-muted-foreground text-xs">Location</div>
                                <div className="font-medium">{system.location}</div>
                              </div>
                              {system.client && (
                                <div>
                                  <div className="text-muted-foreground text-xs">Client</div>
                                  <div className="font-medium">{system.client}</div>
                                </div>
                              )}
                            </div>

                            <div className="text-xs text-muted-foreground">
                              <div className="mb-1">URL: {system.url}</div>
                              {system.lastConnection && (
                                <div>Last Connection: {new Date(system.lastConnection).toLocaleString()}</div>
                              )}
                            </div>
                            
                            {/* Deployment Configuration Details */}
                            {system.architecture_type && (
                              <div className="mt-3 p-3 bg-cp-inset border rounded-lg">
                                <div className="text-xs font-semibold mb-2">Deployment Configuration</div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div>
                                    <span className="text-muted-foreground">Architecture: </span>
                                    <Badge variant="outline" className="text-xs">
                                      {system.architecture_type === 'single_system_multi_client' ? 'Multi-Client' :
                                       system.architecture_type === 'separate_systems' ? 'Separate Systems' :
                                       system.architecture_type === 'btp_spaces' ? 'BTP Spaces' : system.architecture_type}
                                    </Badge>
                                  </div>
                                  {system.dev_client && (
                                    <div>
                                      <span className="text-muted-foreground">Clients: </span>
                                      <span className="font-mono">{system.dev_client}/{system.qa_client}/{system.prod_client}</span>
                                    </div>
                                  )}
                                </div>
                                {system.access_types && (
                                  <div className="mt-2">
                                    <span className="text-muted-foreground text-xs">APIs: </span>
                                    {Object.entries(system.access_types).filter(([k, v]) => v).map(([key]) => (
                                      <Badge key={key} variant="secondary" className="text-xs ml-1">
                                        {key.replace(/_/g, ' ').toUpperCase()}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleTestConnection(system)}
                              disabled={testingConnection}
                            >
                              {testingConnection ? 'Testing...' : 'Test Connection'}
                            </Button>
                            <span title={permissionDeniedTitle(canWriteSapLandscape, 'delete SAP systems')}>
                              <Button 
                                variant="outline" 
                                size="icon"
                                disabled={!canWriteSapLandscape}
                                onClick={() => handleDeleteSystem(system.id)}
                              >
                                <Trash2 className="h-4 w-4 text-cp-crit" />
                              </Button>
                            </span>
                          </div>
                        </div>

                        {connectionStatus && (
                          <Alert className={`mt-3 ${connectionStatus.success ? 'bg-cp-inset border-cp-line' : 'bg-cp-inset border-cp-line'}`}>
                            <AlertDescription className={connectionStatus.success ? 'text-cp-ok' : 'text-cp-crit'}>
                              {connectionStatus.success ? <CheckCircle className="h-4 w-4 inline mr-2" /> : <XCircle className="h-4 w-4 inline mr-2" />}
                              {connectionStatus.message}
                            </AlertDescription>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              <Alert className="mt-4">
                <AlertDescription className="text-xs">
                  <strong>Discovery Integration:</strong> Configured systems will be automatically scanned for:
                  <ul className="list-disc list-inside mt-2 ml-2">
                    <li>Installed SAP modules and components</li>
                    <li>Custom code objects (Z programs, function modules, tables)</li>
                    <li>Clean Core compliance status</li>
                    <li>Integration points and APIs</li>
                    <li>Technical debt analysis</li>
                  </ul>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {canViewSettingsTab(SETTINGS_TABS.MCP_SERVERS, tabAccess) && (
        <TabsContent value={SETTINGS_TABS.MCP_SERVERS} className="space-y-4">
          <SettingsMcpServers />
        </TabsContent>
        )}

        {canViewSettingsTab(SETTINGS_TABS.GENERAL, tabAccess) && (
        <TabsContent value={SETTINGS_TABS.GENERAL} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <SettingsIcon className="h-5 w-5" />
                General Settings
              </CardTitle>
              <CardDescription>Basic platform configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="platformName">Platform Name</Label>
                <Input
                  id="platformName"
                  value={settings.platformName}
                  disabled={!canWriteGeneral}
                  onChange={(e) => updateSetting('platformName', e.target.value)}
                  data-testid="platform-name-input"
                />
              </div>

              <div>
                <Label htmlFor="environment">Environment</Label>
                <Select
                  value={settings.environment}
                  onValueChange={(value) => updateSetting('environment', value)}
                  disabled={!canWriteGeneral}
                >
                  <SelectTrigger id="environment">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="development">Development</SelectItem>
                    <SelectItem value="staging">Staging</SelectItem>
                    <SelectItem value="production">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="timezone">Timezone</Label>
                  <Select
                    value={settings.timezone}
                    onValueChange={(value) => updateSetting('timezone', value)}
                    disabled={!canWriteGeneral}
                  >
                    <SelectTrigger id="timezone">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UTC">UTC</SelectItem>
                      <SelectItem value="America/New_York">Eastern Time</SelectItem>
                      <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                      <SelectItem value="Europe/London">London</SelectItem>
                      <SelectItem value="Asia/Tokyo">Tokyo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="language">Language</Label>
                  <Select
                    value={settings.language}
                    onValueChange={(value) => updateSetting('language', value)}
                    disabled={!canWriteGeneral}
                  >
                    <SelectTrigger id="language">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="de">German</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-4 pt-2 border-t">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="chatConnectedMode">Connected mode for chat</Label>
                    <p className="text-xs text-muted-foreground">
                      When on, Vibe Coding Studio can use live SAP data. When off, chat works without a SAP connection.
                    </p>
                  </div>
                  <span title={permissionDeniedTitle(canWriteGeneral, 'change connected mode')}>
                    <Switch
                      id="chatConnectedMode"
                      checked={settings.chatConnectedMode}
                      disabled={!canWriteGeneral}
                      onCheckedChange={(checked) => updateSetting('chatConnectedMode', checked)}
                      data-testid="chat-connected-mode-switch"
                    />
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="ticketConnectedMode">Connected mode for tickets</Label>
                    <p className="text-xs text-muted-foreground">
                      When on, ticket workflows can use live SAP data. When off, tickets work without a SAP connection.
                    </p>
                  </div>
                  <span title={permissionDeniedTitle(canWriteGeneral, 'change connected mode')}>
                    <Switch
                      id="ticketConnectedMode"
                      checked={settings.ticketConnectedMode}
                      disabled={!canWriteGeneral}
                      onCheckedChange={(checked) => updateSetting('ticketConnectedMode', checked)}
                      data-testid="ticket-connected-mode-switch"
                    />
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {canViewSettingsTab(SETTINGS_TABS.AGENTS, tabAccess) && (
        <TabsContent value={SETTINGS_TABS.AGENTS} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Agent Configuration
              </CardTitle>
              <CardDescription>Configure AI agent behavior and performance</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="defaultLLM">Default LLM Model</Label>
                <Select
                  value={settings.defaultLLM}
                  onValueChange={(value) => updateSetting('defaultLLM', value)}
                  disabled={!canWriteGeneral}
                >
                  <SelectTrigger id="defaultLLM" data-testid="llm-model-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gpt-4o-mini">GPT-4o Mini (Fast)</SelectItem>
                    <SelectItem value="gpt-4o">GPT-4o (Balanced)</SelectItem>
                    <SelectItem value="gpt-5">GPT-5 (Advanced)</SelectItem>
                    <SelectItem value="claude-3-7-sonnet-20250219">Claude 3.7 Sonnet</SelectItem>
                    <SelectItem value="gemini-2.0-flash">Gemini 2.0 Flash</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Model used for all agent tasks</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="agentTimeout">Agent Timeout (seconds)</Label>
                  <Input
                    id="agentTimeout"
                    type="number"
                    value={settings.agentTimeout}
                    disabled={!canWriteGeneral}
                    onChange={(e) => updateSetting('agentTimeout', parseInt(e.target.value))}
                  />
                </div>

                <div>
                  <Label htmlFor="maxRetries">Max Retries</Label>
                  <Input
                    id="maxRetries"
                    type="number"
                    value={settings.maxRetries}
                    disabled={!canWriteGeneral}
                    onChange={(e) => updateSetting('maxRetries', parseInt(e.target.value))}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="parallelAgents">Parallel Agent Execution</Label>
                  <p className="text-xs text-muted-foreground">Execute multiple agents simultaneously</p>
                </div>
                <span title={permissionDeniedTitle(canWriteGeneral, 'change agent settings')}>
                  <Switch
                    id="parallelAgents"
                    checked={settings.parallelAgents}
                    disabled={!canWriteGeneral}
                    onCheckedChange={(checked) => updateSetting('parallelAgents', checked)}
                  />
                </span>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        )}
        {canViewSettingsTab(SETTINGS_TABS.INTEGRATIONS, tabAccess) && (
        <TabsContent value={SETTINGS_TABS.INTEGRATIONS} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                External Integrations
              </CardTitle>
              <CardDescription>Configure connections to external systems</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="jiraUrl">Jira Instance URL</Label>
                <Input
                  id="jiraUrl"
                  value={settings.jiraUrl}
                  disabled={!canWriteGeneral}
                  onChange={(e) => updateSetting('jiraUrl', e.target.value)}
                  placeholder="https://your-domain.atlassian.net"
                />
              </div>

              <div>
                <Label htmlFor="sapBtpUrl">SAP BTP URL</Label>
                <Input
                  id="sapBtpUrl"
                  value={settings.sapBtpUrl}
                  disabled={!canWriteGeneral}
                  onChange={(e) => updateSetting('sapBtpUrl', e.target.value)}
                  placeholder="https://btp.example.com"
                />
              </div>

              <Alert>
                <AlertDescription className="text-xs">
                  Note: These are mock configurations. In production, proper authentication and API keys would be required.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {canViewSettingsTab(SETTINGS_TABS.NOTIFICATIONS, tabAccess) && (
        <TabsContent value={SETTINGS_TABS.NOTIFICATIONS} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Settings
              </CardTitle>
              <CardDescription>Configure how and when to receive notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="emailNotifications">Email Notifications</Label>
                  <p className="text-xs text-muted-foreground">Receive updates via email</p>
                </div>
                <span title={permissionDeniedTitle(canWriteGeneral, 'change notification settings')}>
                  <Switch
                    id="emailNotifications"
                    checked={settings.emailNotifications}
                    disabled={!canWriteGeneral}
                    onCheckedChange={(checked) => updateSetting('emailNotifications', checked)}
                  />
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="slackNotifications">Slack Notifications</Label>
                  <p className="text-xs text-muted-foreground">Send alerts to Slack</p>
                </div>
                <span title={permissionDeniedTitle(canWriteGeneral, 'change notification settings')}>
                  <Switch
                    id="slackNotifications"
                    checked={settings.slackNotifications}
                    disabled={!canWriteGeneral}
                    onCheckedChange={(checked) => updateSetting('slackNotifications', checked)}
                  />
                </span>
              </div>

              {settings.slackNotifications && (
                <div>
                  <Label htmlFor="webhookUrl">Slack Webhook URL</Label>
                  <Input
                    id="webhookUrl"
                    value={settings.webhookUrl}
                    disabled={!canWriteGeneral}
                    onChange={(e) => updateSetting('webhookUrl', e.target.value)}
                    placeholder="https://hooks.slack.com/services/..."
                  />
                </div>
              )}

              <div className="space-y-3 pt-4 border-t">
                <h4 className="text-sm font-medium">Notification Triggers</h4>
                
                <div className="flex items-center justify-between">
                  <Label htmlFor="notifyOnComplete">Ticket Completion</Label>
                  <span title={permissionDeniedTitle(canWriteGeneral, 'change notification settings')}>
                    <Switch
                      id="notifyOnComplete"
                      checked={settings.notifyOnComplete}
                      disabled={!canWriteGeneral}
                      onCheckedChange={(checked) => updateSetting('notifyOnComplete', checked)}
                    />
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="notifyOnFailure">Agent Failures</Label>
                  <span title={permissionDeniedTitle(canWriteGeneral, 'change notification settings')}>
                    <Switch
                      id="notifyOnFailure"
                      checked={settings.notifyOnFailure}
                      disabled={!canWriteGeneral}
                      onCheckedChange={(checked) => updateSetting('notifyOnFailure', checked)}
                    />
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {canViewSettingsTab(SETTINGS_TABS.SECURITY, tabAccess) && (
        <TabsContent value={SETTINGS_TABS.SECURITY} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Security Settings
              </CardTitle>
              <CardDescription>Manage security and compliance settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="requireApproval">Require Deployment Approval</Label>
                  <p className="text-xs text-muted-foreground">Manual approval before production deployment</p>
                </div>
                <span title={permissionDeniedTitle(canWriteGeneral, 'change security settings')}>
                  <Switch
                    id="requireApproval"
                    checked={settings.requireApproval}
                    disabled={!canWriteGeneral}
                    onCheckedChange={(checked) => updateSetting('requireApproval', checked)}
                  />
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="twoFactorAuth">Two-Factor Authentication</Label>
                  <p className="text-xs text-muted-foreground">Require 2FA for all users</p>
                </div>
                <span title={permissionDeniedTitle(canWriteGeneral, 'change security settings')}>
                  <Switch
                    id="twoFactorAuth"
                    checked={settings.twoFactorAuth}
                    disabled={!canWriteGeneral}
                    onCheckedChange={(checked) => updateSetting('twoFactorAuth', checked)}
                  />
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="auditLogging">Comprehensive Audit Logging</Label>
                  <p className="text-xs text-muted-foreground">Log all actions for compliance</p>
                </div>
                <span title={permissionDeniedTitle(canWriteGeneral, 'change security settings')}>
                  <Switch
                    id="auditLogging"
                    checked={settings.auditLogging}
                    disabled={!canWriteGeneral}
                    onCheckedChange={(checked) => updateSetting('auditLogging', checked)}
                  />
                </span>
              </div>

              <div>
                <Label htmlFor="sessionTimeout">Session Timeout (seconds)</Label>
                <Input
                  id="sessionTimeout"
                  type="number"
                  value={settings.sessionTimeout}
                  disabled={!canWriteGeneral}
                  onChange={(e) => updateSetting('sessionTimeout', parseInt(e.target.value))}
                />
                <p className="text-xs text-muted-foreground mt-1">Auto logout after inactivity</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        )}
        {canViewSettingsTab(SETTINGS_TABS.PERFORMANCE, tabAccess) && (
        <TabsContent value={SETTINGS_TABS.PERFORMANCE} className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Performance Optimization</CardTitle>
              <CardDescription>Configure performance and caching settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="cacheEnabled">Enable Caching</Label>
                  <p className="text-xs text-muted-foreground">Cache API responses for faster load times</p>
                </div>
                <span title={permissionDeniedTitle(canWriteGeneral, 'change performance settings')}>
                  <Switch
                    id="cacheEnabled"
                    checked={settings.cacheEnabled}
                    disabled={!canWriteGeneral}
                    onCheckedChange={(checked) => updateSetting('cacheEnabled', checked)}
                  />
                </span>
              </div>

              {settings.cacheEnabled && (
                <div>
                  <Label htmlFor="cacheDuration">Cache Duration (seconds)</Label>
                  <Input
                    id="cacheDuration"
                    type="number"
                    value={settings.cacheDuration}
                    disabled={!canWriteGeneral}
                    onChange={(e) => updateSetting('cacheDuration', parseInt(e.target.value))}
                  />
                </div>
              )}

              <div>
                <Label htmlFor="maxConcurrentTasks">Max Concurrent Tasks</Label>
                <Input
                  id="maxConcurrentTasks"
                  type="number"
                  value={settings.maxConcurrentTasks}
                  disabled={!canWriteGeneral}
                  onChange={(e) => updateSetting('maxConcurrentTasks', parseInt(e.target.value))}
                />
                <p className="text-xs text-muted-foreground mt-1">Maximum parallel operations</p>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="autoRefresh">Auto-Refresh Data</Label>
                  <p className="text-xs text-muted-foreground">Automatically update data in real-time</p>
                </div>
                <span title={permissionDeniedTitle(canWriteGeneral, 'change performance settings')}>
                  <Switch
                    id="autoRefresh"
                    checked={settings.autoRefresh}
                    disabled={!canWriteGeneral}
                    onCheckedChange={(checked) => updateSetting('autoRefresh', checked)}
                  />
                </span>
              </div>

              {settings.autoRefresh && (
                <div>
                  <Label htmlFor="refreshInterval">Refresh Interval (milliseconds)</Label>
                  <Input
                    id="refreshInterval"
                    type="number"
                    value={settings.refreshInterval}
                    disabled={!canWriteGeneral}
                    onChange={(e) => updateSetting('refreshInterval', parseInt(e.target.value))}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}
        {canViewSettingsTab(SETTINGS_TABS.USER_MANAGEMENT, tabAccess) && (
          <TabsContent value={SETTINGS_TABS.USER_MANAGEMENT} className="space-y-4">
            <SettingsUserManagement />
          </TabsContent>
        )}
        {canViewSettingsTab(SETTINGS_TABS.GROUP_MANAGEMENT, tabAccess) && (
          <TabsContent value={SETTINGS_TABS.GROUP_MANAGEMENT} className="space-y-4">
            <SettingsGroupManagement />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
